// React
import React, {useEffect, useRef, useState} from "react";

// External libraries
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {faChevronLeft, faChevronRight, faCalendarAlt, faPen} from "@fortawesome/sharp-solid-svg-icons";

// Internal
import { DropdownAlignment, DropdownDirection, DropdownPanel, DropdownPosition, useDropdownPanel } from "../dropdown-panel/dropdown-panel";
import {FormButton} from "../forms/form-button";
import {FormColumns} from "../forms/form-columns";
import {FormError} from "../forms/form-error";
import {FormNumber} from "../forms/form-number";
import {FormSection} from "../forms/form-section";
import {getDataTestId} from "../functions/data-test-ids";

// CSS
import "./datepicker.css";

const DATE_PICKER_CALENDAR_HEIGHT = 382;

const DatePickerDropDirection = {
    Up: 1,
    Down: 2
};

function getLastDayOfMonth(year, month) {
    const lastDayOfMonth = new Date(year, month, 0);
    return lastDayOfMonth.getDate();
}

function getMonthName(month, labels) {
    switch (month) {
        case 1:
            return labels ? labels.January : "January";
        case 2:
            return labels ? labels.February : "February";
        case 3:
            return labels ? labels.March : "March";
        case 4:
            return labels ? labels.April : "April";
        case 5:
            return labels ? labels.May : "May";
        case 6:
            return labels ? labels.June : "June";
        case 7:
            return labels ? labels.July : "July";
        case 8:
            return labels ? labels.August : "August";
        case 9:
            return labels ? labels.September : "September";
        case 10:
            return labels ? labels.October : "October";
        case 11:
            return labels ? labels.November : "November";
        case 12:
            return labels ? labels.December : "December";
        default:
            return month;
    }
}

function DatePickerYear(props) {
    const selectYear = () => {
        props.OnSelect?.(props.Year);
    };

    return (
        <button type="button" className="eone-ui-datepicker-year" onClick={selectYear} data-testid={getDataTestId(props.DataTestId, props.Year)}>{props.Year}</button>
    );
}

function DatePickerMonth(props) {
    const selectMonth = () => {
        props.OnSelect?.(props.Month);
    };

    return (
        <button type="button" className="eone-ui-datepicker-month" onClick={selectMonth} data-testid={getDataTestId(props.DataTestId, props.Month)}>{getMonthName(props.Month, props.Labels)}</button>
    );
}

function DatePickerDay(props) {
    const selectDay = () => {
        props.OnSelect?.(props.Day);
    };

    const getButtonClass = () => props.Active ? "eone-ui-datepicker-day-active" : "eone-ui-datepicker-day";

    if (props.Day === 0) {
        return (
            <label className="eone-ui-datepicker-day" data-testid={props.DataTestId}/>
        );
    }

    return (
        <button type="button" className={getButtonClass()} onClick={selectDay} data-testid={props.DataTestId}>{props.Day}</button>
    );
}

function DatePickerWeek(props) {
    const selectDay = (day) => {
        props.OnSelect?.(day);
    };

    const getDays = () => {
        const days = [];
        let dayKey = 0;
        if (props.StartDayOfWeek !== 0) {
            for (let pre = 0; pre < props.StartDayOfWeek; pre++) {
                days.push(
                    <DatePickerDay
                        key={dayKey}
                        Day={0}
                        OnSelect={selectDay} 
                        DataTestId={getDataTestId(props.DataTestId, "day", pre)}/>
                );
                dayKey++;
            }
        }
        for (let day = props.StartDay; day <= props.EndDay; day++) {
            days.push(
                <DatePickerDay
                    key={dayKey}
                    Day={day}
                    Active={day === props.SelectedDay}
                    OnSelect={selectDay}
                    DataTestId={getDataTestId(props.DataTestId, "day", day)}/>
            );
            dayKey++;
        }
        if (dayKey < 7) {
            for (let day = dayKey; day < 7; day++) {
                days.push(
                    <DatePickerDay
                        key={dayKey}
                        Day={0}
                        OnSelect={selectDay}
                        DataTestId={getDataTestId(props.DataTestId, "day", day)}/>
                );
                dayKey++;
            }
        }
        return days;
    };

    return (
        <div>
            <div className="eone-ui-datepicker-day-row">
                {getDays()}
            </div>
        </div>
    );
}

function DatePickerYearPicker(props) {
    const [midYear, setMidYear] = useState(0);

    useEffect(() => {
        setMidYear(props.Year);
    }, [props.Year]);

    const selectYear = (year) => {
        props.OnSelect?.(year);
    };

    const back = () => {
        setMidYear(midYear - 9);
    };

    const forward = () => {
        setMidYear(midYear + 9);
    };

    const getYears = (midYear) => {
        return [
            <DatePickerYear key={midYear - 1} Year={midYear - 1} OnSelect={selectYear} DataTestId={props.DataTestId}/>,
            <DatePickerYear key={midYear} Year={midYear} OnSelect={selectYear} DataTestId={props.DataTestId}/>,
            <DatePickerYear key={midYear + 1} Year={midYear + 1} OnSelect={selectYear} DataTestId={props.DataTestId}/>
        ];
    };

    const getClassName = () => {
        let className = "eone-ui-datepicker-calendar-container";
        switch (props.DropDirection) {
            case DatePickerDropDirection.Up:
                className = `${className} eone-ui-datepicker-calendar-container-drop-up`;
                break;
            case DatePickerDropDirection.Down:
                className = `${className} eone-ui-datepicker-calendar-container-drop-down`;
                break;
        }
        return className;
    };
    
    return (
        <div className={getClassName()} style={props.Style}>
            <div className="eone-ui-datepicker-nav-header">
                <button type="button" className="eone-ui-datepicker-nav-button" onClick={back}>
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <label>Select year</label>
                <button type="button" className="eone-ui-datepicker-nav-button" onClick={forward}>
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>
            <div>
                <div className="eone-ui-datepicker-day-row">
                    {getYears(midYear - 3)}
                </div>
            </div>
            <div>
                <div className="eone-ui-datepicker-day-row">
                    {getYears(midYear)}
                </div>
            </div>
            <div>
                <div className="eone-ui-datepicker-day-row">
                    {getYears(midYear + 3)}
                </div>
            </div>
        </div>
    );
}

function DatePickerMonthPicker(props) {
    const [year, setYear] = useState(0);

    useEffect(() => {
        setYear(props.Year);
    }, [props]);

    const selectMonth = (month) => {
        if (typeof props.OnSelect === "function") {
            props.OnSelect(month);
        }
    };

    const back = () => {
        const newYear = year - 1;
        if (newYear > 0) {
            setYear(newYear);
            props.ChangeYear(newYear);
        }
    };

    const forward = () => {
        const newYear = year + 1;
        if (newYear < 9999) {
            setYear(newYear);
            props.ChangeYear(newYear);
        }
    };

    const openYearPicker = () => {
        if (typeof props.OpenYearPicker === "function") {
            props.OpenYearPicker();
        }
    };

    const getClassName = () => {
        let className = "eone-ui-datepicker-calendar-container";
        switch (props.DropDirection) {
            case DatePickerDropDirection.Up:
                className = `${className} eone-ui-datepicker-calendar-container-drop-up`;
                break;
            case DatePickerDropDirection.Down:
                className = `${className} eone-ui-datepicker-calendar-container-drop-down`;
                break;
        }
        return className;
    };
    
    return (
        <div className={getClassName()} style={props.Style}>
            <div className="eone-ui-datepicker-nav-header">
                <button type="button" className="eone-ui-datepicker-nav-button" onClick={back} data-testid={getDataTestId(props.DataTestId, "back-button")}>
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button type="button" className="eone-ui-datepicker-nav-year" onClick={openYearPicker} data-testid={getDataTestId(props.DataTestId, "year")}>{year}</button>
                <button type="button" className="eone-ui-datepicker-nav-button" onClick={forward} data-testid={getDataTestId(props.DataTestId, "forward-button")}>
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>
            <div>
                <div className="eone-ui-datepicker-month-group">
                    <DatePickerMonth Month={1} Labels={props.Labels} OnSelect={selectMonth} DataTestId={props.DataTestId}/>
                    <DatePickerMonth Month={2} Labels={props.Labels} OnSelect={selectMonth} DataTestId={props.DataTestId}/>
                    <DatePickerMonth Month={3} Labels={props.Labels} OnSelect={selectMonth} DataTestId={props.DataTestId}/>
                </div>
            </div>
            <div>
                <div className="eone-ui-datepicker-month-group">
                    <DatePickerMonth Month={4} Labels={props.Labels} OnSelect={selectMonth} DataTestId={props.DataTestId}/>
                    <DatePickerMonth Month={5} Labels={props.Labels} OnSelect={selectMonth} DataTestId={props.DataTestId}/>
                    <DatePickerMonth Month={6} Labels={props.Labels} OnSelect={selectMonth} DataTestId={props.DataTestId}/>
                </div>
            </div>
            <div>
                <div className="eone-ui-datepicker-month-group">
                    <DatePickerMonth Month={7} Labels={props.Labels} OnSelect={selectMonth} DataTestId={props.DataTestId}/>
                    <DatePickerMonth Month={8} Labels={props.Labels} OnSelect={selectMonth} DataTestId={props.DataTestId}/>
                    <DatePickerMonth Month={9} Labels={props.Labels} OnSelect={selectMonth} DataTestId={props.DataTestId}/>
                </div>
            </div>
            <div>
                <div className="eone-ui-datepicker-month-group">
                    <DatePickerMonth Month={10} Labels={props.Labels} OnSelect={selectMonth} DataTestId={props.DataTestId}/>
                    <DatePickerMonth Month={11} Labels={props.Labels} OnSelect={selectMonth} DataTestId={props.DataTestId}/>
                    <DatePickerMonth Month={12} Labels={props.Labels} OnSelect={selectMonth} DataTestId={props.DataTestId}/>
                </div>
            </div>
        </div>
    );
}

function DatePickerDayPicker(props) {
    const [year, setYear] = useState(0);
    const [month, setMonth] = useState(0);

    useEffect(() => {
        setYear(props.Year);
        setMonth(props.Month);
    }, [props.Year, props.Month]);

    const selectDay = (day) => {
        if (typeof props.OnSelect === "function") {
            props.OnSelect(year, month, day);
        }
    };

    const back = () => {
        if (month === 1) {
            setMonth(12);
            setYear(year - 1);
        } else {
            setMonth(month - 1);
        }
    };

    const forward = () => {
        if (month === 12) {
            setMonth(1);
            setYear(year + 1);
        } else {
            setMonth(month + 1);
        }
    };

    const openMonthPicker = () => {
        if (typeof props.OpenMonthPicker === "function") {
            props.OpenMonthPicker();
        }
    };

    const getWeeks = () => {
        const selectedDay = year === props.Year && month === props.Month ? props.Day : 0;
        const weeks = [];
        const daysInMonth = new Date(year, month, 0).getDate();
        const firstDayOfWeek = new Date(year, month - 1, 1).getDay();
        weeks.push(
            <DatePickerWeek
                key="week1"
                StartDayOfWeek={firstDayOfWeek}
                StartDay={1}
                EndDay={7 - firstDayOfWeek}
                SelectedDay={selectedDay}
                OnSelect={selectDay} 
                DataTestId={props.DataTestId} />
        );
        let startDay = 8 - firstDayOfWeek;
        let weekNumber = 2;
        while (startDay <= daysInMonth) {
            weeks.push(
                <DatePickerWeek
                    key={`week${weekNumber}`}
                    StartDayOfWeek={0}
                    StartDay={startDay}
                    EndDay={Math.min(startDay + 6, daysInMonth)}
                    SelectedDay={selectedDay}
                    OnSelect={selectDay}
                    DataTestId={props.DataTestId}/>
            );
            startDay = startDay + 7;
            weekNumber++;
        }
        return weeks;
    };

    const getMonthYear = () => {
        return `${getMonthName(month)} ${year}`;
    };

    const getClassName = () => {
        let className = "eone-ui-datepicker-calendar-container";
        switch (props.DropDirection) {
            case DatePickerDropDirection.Up:
                className = `${className} eone-ui-datepicker-calendar-container-drop-up`;
                break;
            case DatePickerDropDirection.Down:
                className = `${className} eone-ui-datepicker-calendar-container-drop-down`;
                break;
        }
        return className;
    };
    
    return (
        <div className={getClassName()} style={props.Style}>
            <div className="eone-ui-datepicker-nav-header">
                <button type="button" className="eone-ui-datepicker-nav-button" onClick={back} data-testid={getDataTestId(props.DataTestId, "back-button")}>
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button type="button" className="eone-ui-datepicker-nav-year" onClick={openMonthPicker} data-testid={getDataTestId(props.DataTestId, "month")}>{getMonthYear()}</button>
                <button type="button" className="eone-ui-datepicker-nav-button" onClick={forward} data-testid={getDataTestId(props.DataTestId, "forward-button")}>
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>
            {getWeeks()}
        </div>
    );
}

function DatePickerCalendar(props) {
    const [year, setYear] = useState(0);
    const [month, setMonth] = useState(0);
    const [view, setView] = useState("day");

    useEffect(() => {
        setYear(props.Year);
        setMonth(props.Month);
        setView("day");
    }, [props]);

    const selectDate = (yearValue, monthValue, dayValue) => {
        if (typeof props.SelectDate === "function") {
            props.SelectDate(yearValue, monthValue, dayValue);
        }
    };

    const openYearPicker = () => {
        setView("year");
    };

    const openMonthPicker = () => {
        setView("month");
    };

    const changeYear = (value) => {
        setYear(value);
        setView("month");
    };

    const changeMonth = (value) => {
        setMonth(value);
        setView("day");
    };

    if (!props.Open) {
        return null;
    }

    switch (view) {
        case "year":
            return (
                <DatePickerYearPicker
                    Year={year}
                    Style={props.Style}
                    DropDirection={props.DropDirection}
                    OnSelect={changeYear} 
                    DataTestId={getDataTestId(props.DataTestId, "year-picker")} />
            );
        case "month":
            return (
                <DatePickerMonthPicker
                    Year={year}
                    Month={month}
                    Style={props.Style}
                    DropDirection={props.DropDirection}
                    OnSelect={changeMonth} 
                    OpenYearPicker={openYearPicker}
                    ChangeYear={changeYear}
                    DataTestId={getDataTestId(props.DataTestId, "month-picker")}/>
            );
        case "day":
            return (
                <DatePickerDayPicker
                    Year={year}
                    Month={month}
                    Day={props.Day}
                    Style={props.Style}
                    DropDirection={props.DropDirection}
                    OnSelect={selectDate}
                    OpenMonthPicker={openMonthPicker}
                    DataTestId={getDataTestId(props.DataTestId, "day-picker")}/>
            );
        default:
            return null;
    }
}

function DatePickerEditorDay(props) {
    const changeHandler = (value) => {
        if (typeof props.OnChange === "function") {

            let stringValue = value?.toString()?.trim();

            if (/^0+$/.test(stringValue)) {
                // if the string is all 0's then it's just a zero
                stringValue = "0";
            } else {
                // otherwise remove leading zeros
                stringValue = stringValue.replace(/^0+/, "");
            }
            props.OnChange(stringValue);
        }
    };

    const lastDayOfMonth = getLastDayOfMonth(props.Year, props.Month);
    const isDayInvalid = lastDayOfMonth < props.Day;

    return (
        <FormSection Animate>
            <FormNumber
                Label="Day"
                Value={props.Day}
                MinimumValue={1}
                MaximumValue={lastDayOfMonth}
                OnChange={changeHandler}
                DataTestId={getDataTestId(props.DataTestId, "day")}/>
            <FormError
                Message={`There are only ${lastDayOfMonth} days in this month`}
                DataTestId={getDataTestId(props.DataTestId, "day-invalid")}
                Visible={isDayInvalid} />
        </FormSection>
        
    );
}

function DatePickerEditorMonth(props) {
    const changeHandler = (value) => {
        if (typeof props.OnChange === "function") {

            let stringValue = value?.toString()?.trim();

            if (/^0+$/.test(stringValue)) {
                // if the string is all 0's then it's just a zero
                stringValue = "0";
            } else {
                // otherwise remove leading zeros
                stringValue = stringValue.replace(/^0+/, "");
            }

            props.OnChange(stringValue);
        }
    };

    return (
        <FormSection Animate>
            <FormNumber
                Label="Month"
                Value={props.Month}
                MinimumValue={1}
                MaximumValue={12}
                OnChange={changeHandler}
                DataTestId={getDataTestId(props.DataTestId, "month")}/>
            <FormError
                Message="This is not a valid month"
                DataTestId={getDataTestId(props.DataTestId, "month-invalid")}
                Visible={props.Month < 1 || props.Month > 12} />
        </FormSection>
    );
}

function DatePickerEditorYear(props) {
    const changeHandler = (value) => {
        if (typeof props.OnChange === "function") {

            let stringValue = value?.toString()?.trim();

            if (/^0+$/.test(stringValue)) {
                // if the string is all 0's then it's just a zero
                stringValue = "0";
            } else {
                // otherwise remove leading zeros
                stringValue = stringValue.replace(/^0+/, "");
            }
            props.OnChange(stringValue);
        }
    };

    return (
        <FormSection Animate>
            <FormNumber
                Label="Year"
                Value={props.Year}
                MinimumValue={1}
                MaximumValue={9999}
                OnChange={changeHandler}
                DataTestId={getDataTestId(props.DataTestId, "year")} />
            <FormError
                Message="This is not a valid year"
                DataTestId={getDataTestId(props.DataTestId, "year-invalid")}
                Visible={props.Year < 1 || props.Year > 9999} />
        </FormSection>
    );
}

function DatePickerEditorValues(props) {
    const changeDay = (value) => {
        if (typeof props.ChangeDay === "function") {
            props.ChangeDay(value);
        }
    };

    const changeMonth = (value) => {
        if (typeof props.ChangeMonth === "function") {
            props.ChangeMonth(value);
        }
    };

    const changeYear = (value) => {
        if (typeof props.ChangeYear === "function") {
            props.ChangeYear(value);
        }
    };

    switch (props.DateFormat) {
        case "mdy":
            return (
                <div>
                    <DatePickerEditorMonth Month={props.Month} OnChange={changeMonth} DataTestId={props.DataTestId}/>
                    <DatePickerEditorDay Day={props.Day} Month={props.Month} Year={props.Year} OnChange={changeDay} DataTestId={props.DataTestId}/>
                    <DatePickerEditorYear Year={props.Year} OnChange={changeYear} DataTestId={props.DataTestId}/>
                </div>
            );
        case "dmy":
            return (
                <div>
                    <DatePickerEditorDay Day={props.Day} Month={props.Month} Year={props.Year} OnChange={changeDay} DataTestId={props.DataTestId}/>
                    <DatePickerEditorMonth Month={props.Month} OnChange={changeMonth} DataTestId={props.DataTestId}/>
                    <DatePickerEditorYear Year={props.Year} OnChange={changeYear} DataTestId={props.DataTestId}/>
                </div>
            );
        case "ymd":
            return (
                <div>
                    <DatePickerEditorYear Year={props.Year} OnChange={changeYear} DataTestId={props.DataTestId}/>
                    <DatePickerEditorMonth Month={props.Month} OnChange={changeMonth} DataTestId={props.DataTestId}/>
                    <DatePickerEditorDay Day={props.Day} Month={props.Month} Year={props.Year} OnChange={changeDay} DataTestId={props.DataTestId}/>
                </div>
            );
        case "ydm":
            return (
                <div>
                    <DatePickerEditorYear Year={props.Year} OnChange={changeYear} DataTestId={props.DataTestId}/>
                    <DatePickerEditorDay Day={props.Day} Month={props.Month} Year={props.Year} OnChange={changeDay} DataTestId={props.DataTestId}/>
                    <DatePickerEditorMonth Month={props.Month} OnChange={changeMonth} DataTestId={props.DataTestId}/>
                </div>
            );
        case "myd":
            return (
                <div>
                    <DatePickerEditorMonth Month={props.Month} OnChange={changeMonth} DataTestId={props.DataTestId}/>
                    <DatePickerEditorYear Year={props.Year} OnChange={changeYear} DataTestId={props.DataTestId}/>
                    <DatePickerEditorDay Day={props.Day} Month={props.Month} Year={props.Year} OnChange={changeDay} DataTestId={props.DataTestId}/>
                </div>
            );
        case "dym":
            return (
                <div>
                    <DatePickerEditorDay Day={props.Day} Month={props.Month} Year={props.Year} OnChange={changeDay} DataTestId={props.DataTestId}/>
                    <DatePickerEditorYear Year={props.Year} OnChange={changeYear} DataTestId={props.DataTestId}/>
                    <DatePickerEditorMonth Month={props.Month} OnChange={changeMonth} DataTestId={props.DataTestId}/>
                </div>
            );
        default:
            return (
                <div>
                    <DatePickerEditorMonth Month={props.Month} OnChange={changeMonth} DataTestId={props.DataTestId}/>
                    <DatePickerEditorDay Day={props.Day} Month={props.Month} Year={props.Year} OnChange={changeDay} DataTestId={props.DataTestId}/>
                    <DatePickerEditorYear Year={props.Year} OnChange={changeYear} DataTestId={props.DataTestId}/>
                </div>
            );
    }
}

function DatePickerEditor(props) {
    const [year, setYear] = useState(0);
    const [month, setMonth] = useState(0);
    const [day, setDay] = useState(0);

    useEffect(() => {
        setYear(props.Year);
        setMonth(props.Month);
        setDay(props.Day);
    }, [props]);

    const save = () => {
        props.SelectDate(year, month, day);
    };

    const getDateFormat = () => {
        if (!props.Locale) return "mdy";
        const dateTimeFormat = new Intl.DateTimeFormat(props.Locale);
        // Only the ordering of parts matters for format detection, so use a fixed valid reference date.
        const dateValue = new Date(2000, 0, 1);
        const parts = dateTimeFormat.formatToParts(dateValue);
        let dateFormat = "";
        for (let i = 0, len = parts.length; i < len; i++) {
            if (parts[i].type === "day") {
                dateFormat = `${dateFormat}d`;
            }
            if (parts[i].type === "month") {
                dateFormat = `${dateFormat}m`;
            }
            if (parts[i].type === "year" || parts[i].type === "yearName") {
                dateFormat = `${dateFormat}y`;
            }
        }
        return dateFormat;
    };
    
    const isDateValid = () => {
        if (!year) return false;
        if (!month) return false;
        if (!day) return false;
        if (month < 1 || month > 12) return false;
        if (day < 1 || day > getLastDayOfMonth(year, month)) return false;
        return true;
    };

    const getClassName = () => {
        let className = "eone-ui-datepicker-editor-container";
        switch (props.DropDirection) {
            case DatePickerDropDirection.Up:
                className = `${className} eone-ui-datepicker-editor-container-drop-up`;
                break;
            case DatePickerDropDirection.Down:
                className = `${className} eone-ui-datepicker-editor-container-drop-down`;
                break;
        }
        return className;
    };
    
    if (!props.Open) {
        return null;
    }

    const dateFormat = getDateFormat();
    return (
        <div className={getClassName()} style={props.Style}>
            <DatePickerEditorValues
                Day={day}
                Month={month}
                Year={year}
                DateFormat={dateFormat}
                ChangeDay={setDay}
                ChangeMonth={setMonth}
                ChangeYear={setYear}
                DataTestId={props.DataTestId} />
            <FormColumns Columns={2}>
                <FormButton
                    Label="Save"
                    Class="eone-ui-form-button-primary eone-ui-button-container-secondary"
                    Disabled={!isDateValid()}
                    Inline={false}
                    OnClick={save}
                    DataTestId={getDataTestId(props.DataTestId, "save")}/>
                <FormButton
                    Label="Cancel"
                    Class="eone-ui-form-button-secondary eone-ui-button-container-tertiary"
                    Inline={false}
                    OnClick={props.Cancel}
                    DataTestId={getDataTestId(props.DataTestId, "cancel")}/>
            </FormColumns>
        </div>
    );
}

function DatePicker(props) {
    const [year, setYear] = useState(0);
    const [month, setMonth] = useState(0);
    const [day, setDay] = useState(0);
    const [isEmpty, setIsEmpty] = useState(false);
    const [menu, setMenu] = useState("calendar");

    const {
        open,
        close,
        toggleOpen,
        triggerRef,
        style,
        direction,
    } = useDropdownPanel(
        DropdownPosition.Below,
        DropdownDirection.Up,
        DropdownAlignment.AlignLeftAndRight,
        1,
        DATE_PICKER_CALENDAR_HEIGHT,
        DATE_PICKER_CALENDAR_HEIGHT,
        props.OverrideTriggerRef
    );

    const dropdownPanelRef = useRef(null);

    const setDateValues = (yearValue, monthValue, dayValue) => {
        setYear(yearValue);
        setMonth(monthValue);
        setDay(dayValue);
        setIsEmpty(false);
        close();
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            const outsideTrigger = triggerRef?.current && !triggerRef.current.contains(e.target);
            const outsidePanel = dropdownPanelRef.current && !dropdownPanelRef.current.contains(e.target);
            if (outsideTrigger && outsidePanel) {
                close();
            }
        };
        if (open) {
            window.addEventListener("mousedown", handleClickOutside);
        }
        return () => window.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    useEffect(() => {
        const today = new Date();
        if (props.Value) {
            if ((props.Value instanceof Date)) {
                setDateValues(props.Value.getFullYear(), props.Value.getMonth() + 1, props.Value.getDate());
            } else {
                const valueParts = props.Value.toString().split("-");
                if (valueParts.length === 3) {
                    setDateValues(parseInt(valueParts[0]), parseInt(valueParts[1]), parseInt(valueParts[2]));
                } else {
                    setDateValues(today.getFullYear(), today.getMonth() + 1, today.getDate());
                }
            }
        } else if (props.AllowEmpty) {
            // Seed today so the calendar/editor opens on the current month, but render blank.
            setDateValues(today.getFullYear(), today.getMonth() + 1, today.getDate());
            setIsEmpty(true);
        } else {
            setDateValues(today.getFullYear(), today.getMonth() + 1, today.getDate());
        }
    }, [props.Value]);

    const getValue = (year, month, day, display) => {
        if (display) {
            const date = new Date(year, month - 1, day);
            if (props.Locale) {
                return new Intl.DateTimeFormat(props.Locale).format(date);
            } else {
                return new Intl.DateTimeFormat().format(date);
            }
        } else {
            return `${year}-${month}-${day}`;
        }
    };

    const selectDate = (yearValue, monthValue, dayValue) => {
        setDateValues(yearValue, monthValue, dayValue);
        if (typeof props.OnChange === "function") {
            props.OnChange(getValue(yearValue, monthValue, dayValue, false));
        }
    };


    // The open state has to be visible to CSS: the dropdown portals to document.body, so a cell that
    // only reveals its buttons on :hover / :focus-within loses them the moment the pointer moves into
    // the calendar. Inert on its own - only the in-table variant styles this class.
    const getContainerClass = () => {
        const containerClass = props.ContainerClass || "eone-ui-datepicker-container";
        return open ? `${containerClass} eone-ui-datepicker-container-open` : containerClass;
    };

    const toggleDropdown = () => {
        toggleOpen();
        setMenu("calendar");
    };

    const toggleEditor = () => {
        toggleOpen();
        setMenu("editor");
    };

    const changeHandler = () => {
        if (typeof props.OnChange === "function") {
            props.OnChange(getValue(year, month, day, false));
        }
    };

    if (props.Visible !== undefined && !props.Visible) {
        return null;
    }

    if (props.ReadOnly) {
        return (
            <div className={props.ContainerClass || "eone-ui-datepicker-container"} >
                <input
                    type="text"
                    readOnly
                    className="eone-ui-datepicker-value"
                    value={isEmpty ? "" : getValue(year, month, day, true)}
                    placeholder={props.Placeholder}
                    data-testid={props.DataTestId} />
            </div>
        );
    }

    return (
        <div className={getContainerClass()} ref={triggerRef}>
            <input
                type="text"
                readOnly
                className="eone-ui-datepicker-value"
                value={isEmpty ? "" : getValue(year, month, day, true)}
                placeholder={props.Placeholder}
                onChange={changeHandler}
                data-testid={props.DataTestId} />
            <button type="button" className="eone-ui-datepicker-button" onClick={toggleEditor} data-testid={getDataTestId(props.DataTestId, "edit-button")}>
                <FontAwesomeIcon icon={faPen} />
            </button>
            <button type="button" className="eone-ui-datepicker-button" onClick={toggleDropdown} data-testid={getDataTestId(props.DataTestId, "calendar-button")}>
                <FontAwesomeIcon icon={faCalendarAlt} />
            </button>
            <DropdownPanel Style={style} Open={open} Close={close} DropdownPanelRef={dropdownPanelRef}>
                <DatePickerCalendar
                    Open={open && (menu === "calendar")}
                    Year={year}
                    Month={month}
                    Day={day}
                    DropDirection={direction}
                    SelectDate={selectDate}
                    DataTestId={getDataTestId(props.DataTestId, "calendar")} />
                <DatePickerEditor
                    Open={open && (menu === "editor")}
                    Year={year}
                    Month={month}
                    Day={day}
                    Locale={props.Locale}
                    DropDirection={direction}
                    SelectDate={selectDate}
                    Cancel={toggleEditor}
                    DataTestId={getDataTestId(props.DataTestId, "editor")} />
            </DropdownPanel>
        </div>
    );
}

export { DatePicker, getMonthName };